/**
 * Protected route component
 * Enhanced to handle onboarding flow
 */

import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { useRequireAuthWithOnboarding } from '@/hooks/useAuthCheck';

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const { state, isLoading } = useRequireAuthWithOnboarding('/auth/login');

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="border-primary-600 h-12 w-12 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  // The useRequireAuthWithOnboarding hook handles all redirects:
  // - Unauthenticated users -> /auth/login
  // - Users needing onboarding -> /onboarding
  // - Authenticated users with org -> continue

  // If we're in an unauthenticated state, redirect to login
  if (state === 'unauthenticated') {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Render children or outlet for authenticated or needs-onboarding states
  // The hook will handle redirecting to onboarding if needed
  return children || <Outlet />;
}
